<template>
	<div class="toast-notification" :class="notificationClass">
		<div class="toast-icon">
			<svg
				v-if="type === 'success'"
				viewBox="0 0 20 20"
				fill="currentColor"
			>
				<path
					fill-rule="evenodd"
					d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
					clip-rule="evenodd"
				/>
			</svg>
			<svg
				v-else-if="type === 'error'"
				viewBox="0 0 20 20"
				fill="currentColor"
			>
				<path
					fill-rule="evenodd"
					d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
					clip-rule="evenodd"
				/>
			</svg>
			<svg
				v-else-if="type === 'warning'"
				viewBox="0 0 20 20"
				fill="currentColor"
			>
				<path
					fill-rule="evenodd"
					d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
					clip-rule="evenodd"
				/>
			</svg>
			<svg v-else viewBox="0 0 20 20" fill="currentColor">
				<path
					fill-rule="evenodd"
					d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
					clip-rule="evenodd"
				/>
			</svg>
		</div>

		<div class="toast-content">
			<h4 class="toast-title">{{ title }}</h4>
			<div class="toast-message" v-html="message"></div>
		</div>

		<button
			type="button"
			class="toast-close"
			@click="close()"
			aria-label="Close notification"
		>
			<svg viewBox="0 0 20 20" fill="currentColor">
				<path
					fill-rule="evenodd"
					d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
					clip-rule="evenodd"
				/>
			</svg>
		</button>

		<div class="toast-progress" :class="'progress-' + type"></div>
	</div>
</template>

<script>
export default {
	props: {
		type: { default: 'success' },
		title: { default: 'Store Created' },
		message: { default: 'The store has been added successfully' },
		id: { default: '' },
		duration: { default: 5000 },
	},
	computed: {
		notificationClass() {
			return {
				'toast-success': this.type === 'success',
				'toast-error': this.type === 'error',
				'toast-warning': this.type === 'warning',
				'toast-info': this.type === 'info',
			};
		},
	},
	methods: {
		close() {
			Event.$emit('notification:close', { id: this.id });
		},
	},
	mounted() {
		if (this.duration > 0) {
			setTimeout(() => {
				this.close();
			}, this.duration);
		}
	},
};
</script>

<style lang="less">
.toast-notification {
	position: relative;
	display: flex;
	align-items: flex-start;
	gap: 12px;
	padding: 16px 20px;
	margin-bottom: 12px;
	background: #ffffff;
	border-radius: 12px;
	box-shadow:
		0 4px 12px rgba(0, 0, 0, 0.15),
		0 2px 4px rgba(0, 0, 0, 0.1);
	border-left: 4px solid;
	min-height: 64px;
	overflow: hidden;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	animation: slideInRight 0.4s cubic-bezier(0.4, 0, 0.2, 1);
	backdrop-filter: blur(10px);

	&:hover {
		transform: translateY(-2px);
		box-shadow:
			0 8px 25px rgba(0, 0, 0, 0.2),
			0 4px 8px rgba(0, 0, 0, 0.15);
	}

	// Type-specific styles
	&.toast-success {
		border-left-color: #10b981;
		background: linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%);

		.toast-icon {
			color: #10b981;
		}

		.toast-title {
			color: #065f46;
		}

		.progress-success {
			background: linear-gradient(90deg, #10b981, #34d399);
		}
	}

	&.toast-error {
		border-left-color: #ef4444;
		background: linear-gradient(135deg, #ffffff 0%, #fef2f2 100%);

		.toast-icon {
			color: #ef4444;
		}

		.toast-title {
			color: #991b1b;
		}

		.progress-error {
			background: linear-gradient(90deg, #ef4444, #f87171);
		}
	}

	&.toast-warning {
		border-left-color: #f59e0b;
		background: linear-gradient(135deg, #ffffff 0%, #fffbeb 100%);

		.toast-icon {
			color: #f59e0b;
		}

		.toast-title {
			color: #92400e;
		}

		.progress-warning {
			background: linear-gradient(90deg, #f59e0b, #fbbf24);
		}
	}

	&.toast-info {
		border-left-color: #3b82f6;
		background: linear-gradient(135deg, #ffffff 0%, #eff6ff 100%);

		.toast-icon {
			color: #3b82f6;
		}

		.toast-title {
			color: #1e40af;
		}

		.progress-info {
			background: linear-gradient(90deg, #3b82f6, #60a5fa);
		}
	}
}

.toast-icon {
	flex-shrink: 0;
	width: 24px;
	height: 24px;
	margin-top: 2px;

	svg {
		width: 100%;
		height: 100%;
		transition: transform 0.2s ease;
	}
}

.toast-content {
	flex: 1;
	min-width: 0;
}

.toast-title {
	font-size: 16px;
	font-weight: 600;
	margin: 0 0 4px 0;
	line-height: 1.4;
	letter-spacing: -0.01em;
}

.toast-message {
	font-size: 14px;
	color: #6b7280;
	line-height: 1.5;
	margin: 0;

	// Handle HTML content gracefully
	p {
		margin: 0;

		&:not(:last-child) {
			margin-bottom: 8px;
		}
	}

	a {
		color: inherit;
		text-decoration: underline;

		&:hover {
			opacity: 0.8;
		}
	}
}

.toast-close {
	flex-shrink: 0;
	width: 24px;
	height: 24px;
	padding: 0;
	margin: 0;
	background: transparent;
	border: none;
	color: #6b7280;
	cursor: pointer;
	border-radius: 6px;
	transition: all 0.2s ease;

	&:hover {
		color: #111827;
		background: rgba(0, 0, 0, 0.05);
		transform: scale(1.1);
	}

	&:active {
		transform: scale(0.95);
	}

	svg {
		width: 100%;
		height: 100%;
	}
}

.toast-progress {
	position: absolute;
	bottom: 0;
	left: 0;
	height: 3px;
	width: 100%;
	border-radius: 0 0 12px 12px;
	opacity: 0.8;
	animation: progressBar 5s linear forwards;
}

// Animations
@keyframes slideInRight {
	0% {
		opacity: 0;
		transform: translateX(100%);
	}
	100% {
		opacity: 1;
		transform: translateX(0);
	}
}

@keyframes progressBar {
	0% {
		transform: scaleX(1);
	}
	100% {
		transform: scaleX(0);
		transform-origin: left;
	}
}

// Responsive design
@media (max-width: 480px) {
	.toast-notification {
		padding: 14px 16px;
		margin-bottom: 8px;
		border-radius: 8px;

		.toast-title {
			font-size: 15px;
		}

		.toast-message {
			font-size: 13px;
		}

		.toast-icon {
			width: 20px;
			height: 20px;
		}

		.toast-close {
			width: 20px;
			height: 20px;
		}
	}
}

// Dark mode support
@media (prefers-color-scheme: dark) {
	.toast-notification {
		background: #1f2937;
		color: #f9fafb;

		.toast-message {
			color: #45484e;
		}

		.toast-close {
			color: #45484e;

			&:hover {
				color: #45484e;
				background: rgba(255, 255, 255, 0.1);
			}
		}
	}
}
</style>
